import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import type { HistoryCoursesFilterI } from 'types';
import usePrioritiesApi from './usePrioritiesApi';
import useCourseCategoriesApi from './useCourseCategoriesApi';
import useDebounce from './useDebounce';

interface Props {
  onSearch: (filters: HistoryCoursesFilterI) => void;
}

const defaultValue: HistoryCoursesFilterI = {};

const useHistoryCoursesFilters = ({ onSearch }: Props) => {
  const isFirstRender = useRef(true);

  const { control, reset, getValues, watch } = useForm({
    defaultValues: defaultValue,
  });

  const { data: prioritiesData, loading: prioritiesLoading, error: prioritiesError } = usePrioritiesApi({ init: true });

  const {
    data: categoriesData,
    loading: categoriesLoading,
    error: categoriesError,
  } = useCourseCategoriesApi({ init: true });

  const handleReset = () => {
    reset(defaultValue);
    onSearch({});
  };

  // Watch for changes in the name field
  const nameValue = watch('name');
  const priorityIdValue = watch('priorityId');
  const categoryIdValue = watch('categoryId');
  const debouncedName = useDebounce(nameValue, 500);

  // Determinar si hay filtros activos
  const hasActiveFilters = !!(nameValue || priorityIdValue || categoryIdValue);

  // Effect to handle debounced search
  useEffect(() => {
    // Evitar ejecución en la primera renderización
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Ejecutar búsqueda cuando el usuario cambia el texto (incluso si lo borra)
    if (debouncedName !== undefined) {
      const filters = getFiltersForApi();
      onSearch(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedName]);

  // Handle other filter changes immediately
  const handleFilterChange = () => {
    const filters = getFiltersForApi();
    onSearch(filters);
  };

  const getFiltersForApi = () => {
    const values = getValues();
    const filters: HistoryCoursesFilterI = {};

    if (values.name) filters.name = values.name;
    if (values.priorityId) filters.priorityId = values.priorityId;
    if (values.categoryId) filters.categoryId = values.categoryId;

    return filters;
  };

  return {
    control,
    isDirty: hasActiveFilters,
    handleReset,
    handleFilterChange,
    getFiltersForApi,
    // Priorities
    prioritiesData,
    prioritiesLoading,
    prioritiesError,
    // Course categories
    categoriesData,
    categoriesLoading,
    categoriesError,
  };
};

export default useHistoryCoursesFilters;
